import type {
  SimpleMdmApp,
  AppDetailResponse,
  LatestVersionInfo,
  JobListItem,
  JobDetail,
  LatestVersion,
  CreateJobRequest,
  RetryJobRequest,
  DeviceRecordFilter,
} from "@meridian-mdm/shared";

const BASE_URL = import.meta.env.VITE_API_URL;

let _getAccessToken: (() => Promise<string | undefined>) | null = null;

export function setAccessTokenGetter(fn: () => Promise<string | undefined>) {
  _getAccessToken = fn;
}

async function request<T>(path: string, options: RequestInit = {}): Promise<T> {
  const token = _getAccessToken ? await _getAccessToken() : undefined;

  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    ...((options.headers as Record<string, string>) ?? {}),
  };

  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  }

  const url = `${BASE_URL}${path}`;
  const res = await fetch(url, { ...options, headers });

  if (!res.ok) {
    const body = await res.text().catch(() => "");
    let message = `Request failed: ${res.status} ${res.statusText}`;
    try {
      const parsed = JSON.parse(body);
      if (parsed.error) message = parsed.error;
      else if (parsed.message) message = parsed.message;
    } catch {
      if (body) message = body;
    }
    throw new ApiError(message, res.status);
  }

  if (res.status === 204) return undefined as T;
  const json = await res.json();
  // Backend wraps responses in { data: ... } — unwrap automatically
  if (json && typeof json === "object" && "data" in json) {
    return json.data as T;
  }
  return json as T;
}

export class ApiError extends Error {
  constructor(
    message: string,
    public status: number,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

// ── Apps ────────────────────────────────────────────────────────────

export function fetchApps(): Promise<SimpleMdmApp[]> {
  return request<SimpleMdmApp[]>("/v1/admin/apps");
}

export function fetchAppDetail(appId: number): Promise<AppDetailResponse> {
  return request<AppDetailResponse>(`/v1/admin/apps/${appId}/installs`);
}

export function syncLatestVersion(appId: number): Promise<LatestVersionInfo> {
  return request<LatestVersionInfo>(`/v1/admin/apps/${appId}/sync-latest-version`, {
    method: "POST",
  });
}

// ── Jobs ────────────────────────────────────────────────────────────

export function fetchJobs(): Promise<JobListItem[]> {
  return request<JobListItem[]>("/v1/admin/jobs");
}

export function fetchJob(jobId: number, params?: Partial<DeviceRecordFilter>): Promise<JobDetail> {
  const search = new URLSearchParams();
  if (params?.page) search.set("page", String(params.page));
  if (params?.limit) search.set("limit", String(params.limit));
  if (params?.status) search.set("status", params.status);
  if (params?.search) search.set("search", params.search);
  if (params?.sortBy) search.set("sortBy", params.sortBy);
  if (params?.sortDir) search.set("sortDir", params.sortDir);
  const qs = search.toString();
  return request<JobDetail>(`/v1/admin/jobs/${jobId}${qs ? `?${qs}` : ""}`);
}

export function createJob(data: CreateJobRequest): Promise<{ jobId: number }> {
  return request<{ jobId: number }>("/v1/admin/jobs", {
    method: "POST",
    body: JSON.stringify(data),
  });
}

export function retryJob(jobId: number, statuses: RetryJobRequest["statuses"]): Promise<void> {
  return request<void>(`/v1/admin/jobs/${jobId}/retry`, {
    method: "POST",
    body: JSON.stringify({ statuses }),
  });
}

/** Start an admin delete + reinstall remediation for a single device record. */
export function reinstallDevice(jobId: number, recordId: number): Promise<{ status: string }> {
  return request<{ status: string }>(`/v1/admin/jobs/${jobId}/records/${recordId}/reinstall`, {
    method: "POST",
  });
}

// ── Latest Versions ─────────────────────────────────────────────────

export function fetchLatestVersions(): Promise<LatestVersion[]> {
  return request<LatestVersion[]>("/v1/admin/latest-versions");
}

export function updateLatestVersion(bundleId: string, version: string): Promise<void> {
  return request<void>(`/v1/admin/latest-versions/${encodeURIComponent(bundleId)}`, {
    method: "PUT",
    body: JSON.stringify({ version }),
  });
}
